import { styled } from "styled-components";

import { type ManualPresetKey } from "@vericus/cadmus-manual";
import { Button, LinkButton, Modal, Spacer, Text } from "@vericus/cadmus-ui";

import { SubmissionDeclaration } from "@/components/submission-declaration";
import { type AssessmentNoun } from "@/features/assignment";
import { AuthorityState } from "@/features/authority";
import { TaskNoticeFragment } from "@/generated/graphql";

export interface TimedSubmitModalProps {
  /**
   * The work ID for localStorage acceptance tracking.
   */
  workId: string;
  /**
   * There is a prior final submission.
   */
  hasFinal: boolean;
  /**
   * Can make a late submission in the current state.
   */
  canSubmitLateExam: boolean;
  /**
   * Number of minutes of late submission allowance. A `null` value means
   * un-bounded limit if `canSubmitLateExam` is true.
   */
  extraSubmissionTime: number | null;
  /**
   * Is the generative AI agreement required for this submission.
   */
  requireGenAiAgreement: boolean;
  /** Canonical display term for this assessment in UI copy. */
  assessmentNoun: AssessmentNoun;
  /**
   * Are the required submission declarations checked?.
   */
  hasCheckedPledges: AuthorityState["hasAcceptedSubmissionDeclarations"];
  /**
   * Callback to update the checked value of the a submission declaration agreement.
   */
  onUpdateSubmissionDeclaration: (
    declarationType: keyof AuthorityState["hasAcceptedSubmissionDeclarations"],
    hasAgreed: boolean
  ) => void;
  /**
   * Callback when submit now action is invoked.
   */
  onSubmitNowClick: VoidFunction;
  /**
   * Callback when submit later action is invoked.
   */
  onSubmitLateClick: VoidFunction;
  open: boolean;
  onExitComplete?: VoidFunction;
  /**
   * GenAI guideline notice attached to the task, if any.
   */
  genaiGuidelineNotice?: TaskNoticeFragment;
  /**
   * Callback to open the manual preset viewer.
   */
  onOpenManualPreset: (key: ManualPresetKey) => void;
}

/**
 * Modal component which displays submission choices for timed assignments and
 * exams.
 */
export function TimedSubmitModal(props: TimedSubmitModalProps) {
  const {
    workId,
    hasFinal,
    canSubmitLateExam,
    extraSubmissionTime,
    hasCheckedPledges,
    requireGenAiAgreement,
    onUpdateSubmissionDeclaration,
    onSubmitLateClick,
    onSubmitNowClick,
    open,
    onExitComplete,
    genaiGuidelineNotice,
    onOpenManualPreset,
  } = props;

  const isAgreementChecked =
    hasCheckedPledges.academicIntegrity &&
    (requireGenAiAgreement ? hasCheckedPledges.generativeAi : true);

  let message = null;
  if (canSubmitLateExam && extraSubmissionTime != null) {
    message = (
      <>
        Submit what you have now, or continue working. <br />
        You can submit up to {extraSubmissionTime} minutes late, but you may
        incur a penalty. <br />
        After {extraSubmissionTime} minutes you will not be able to submit.
      </>
    );
  } else if (canSubmitLateExam) {
    message = (
      <>
        Submit what you have now, or continue working. <br />
        You can submit late, but you may incur a penalty.
      </>
    );
  } else if (!canSubmitLateExam && hasFinal) {
    message = (
      <>
        {"You’ve"} already made a submission for this {props.assessmentNoun}.{" "}
        <br />
        Do you want to resubmit with your latest changes?
      </>
    );
  }

  return (
    <Modal.Root open={open}>
      <StyledModalContent
        withCloseButton={false}
        onExitComplete={onExitComplete}
      >
        <Modal.Title asChild>
          <Text kind="headingOne">{"Time's"} up!</Text>
        </Modal.Title>
        <Modal.Description asChild>
          <Text kind="bodyMd">{message}</Text>
        </Modal.Description>

        <SubmissionDeclaration
          acceptanceScopeId={workId}
          onUpdateSubmissionDeclaration={onUpdateSubmissionDeclaration}
          hasCheckedPledges={hasCheckedPledges}
          requireGenAiAgreement={requireGenAiAgreement}
          genaiGuidelineNotice={genaiGuidelineNotice}
          onOpenManualPreset={onOpenManualPreset}
        />
        <div>
          <ActionButton
            fullWidth
            kind="primary"
            disabled={!isAgreementChecked}
            size="lg"
            iconName="Send"
            iconPosition="right"
            onClick={onSubmitNowClick}
            aria-label={"Submit exam now"}
          >
            Submit now
          </ActionButton>
          <Spacer spacing={16} />
          {canSubmitLateExam && (
            <LinkButton
              onClick={onSubmitLateClick}
              aria-label="Close modal and keep writing"
            >
              Continue working and submit late
            </LinkButton>
          )}
          {!canSubmitLateExam && hasFinal && (
            <ActionButton
              size="lg"
              aria-label="Close modal and keep previous submission"
              onClick={onSubmitLateClick}
            >
              Discard and keep my previous submission
            </ActionButton>
          )}
        </div>
      </StyledModalContent>
    </Modal.Root>
  );
}

const StyledModalContent = styled(Modal.Content)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 64px 48px;
  overflow-y: auto;
`;

const ActionButton = styled(Button)`
  width: 336px;
`;
